/**
 * StageChips — one pressable chip per stage this search has, with its count.
 *
 * The chips are the Candidates tab's first filter: a reader lands on the tab,
 * sees "Rejected 9 · Identified 6 · Pursuing 3 · Placed 1" and presses one.
 * They toggle the SAME `stage[]` filter the toolbar's multi-select writes to
 * the URL, so pressing a chip and ticking the box are one action with two
 * controls — the chip is the fast path, the menu is the complete one (it also
 * offers Highest stage, which has no chips).
 *
 * The counts come from `report.current_stage_distribution`, the donut's own
 * numbers, rather than from the candidates endpoint: that endpoint answers for
 * the FILTERED set, and a chip row that renumbered itself every time one was
 * pressed would be a row of moving targets. The distribution counts every
 * countable candidacy once, so the chips add up to the table's unfiltered
 * total and stay put while the reader narrows.
 *
 * ORDER: the ladder descending (the tab's default row order, furthest-along
 * first), then the off-ladder stages A-Z with `Rejected` last of them, then
 * Unknown. This is a MIRROR of the backend's `stage_sort_key`
 * (`nu_assist_shared/domain/search_report.py`), which orders the table's rows
 * and the stage menu by the same rule — one rule, so the chip row and the row
 * order cannot disagree when a tenant adds an off-ladder stage that sorts
 * after "Rejected" alphabetically ("Withdrawn"). The two are kept in sync by
 * hand, like `LADDER_STAGES`; `stageChipOrder`'s test pins the same fixture
 * the Python test pins. The off-ladder A-Z is `toLowerCase()` + code-unit
 * order here and `lower()` + code-point order there — the SAME normalisation
 * on both sides (not `localeCompare`, not `casefold`), so parity holds past
 * ASCII too. Rejected closes the group because it is the biggest bucket on
 * most searches and the one a reader excludes rather than finds.
 *
 * The colour dot is the palette's (`chartPalette.ts`): a chip, its
 * pill in the table and its slice in the donut agree, because all three are
 * the same function of the stage name.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchReportBucket } from "@nu-assist/contracts";

import { stageBgClass } from "./chartPalette";
import { LADDER_STAGES, REJECTED_STAGE, UNKNOWN_STAGE } from "./ladder";

export interface StageChipsProps {
  /** Every stage present, with its count: `report.current_stage_distribution`
   *  on the Candidates tab, `counts.stages` on a Thrive-sourced People List. */
  distribution: readonly Pick<SearchReportBucket, "label" | "count">[];
  /** The `stage[]` values currently applied. */
  selected: readonly string[];
  onToggle: (stage: string) => void;
}

/**
 * `stage_sort_key`'s (group, rank, label) for one stage, with the ladder rank
 * NEGATED so that a plain ascending sort walks the ladder downwards — the
 * table's `stage desc`, the tab's default order.
 */
function chipSortKey(stage: string): [number, number, string] {
  if (stage === UNKNOWN_STAGE) return [2, 0, ""];
  const rank = LADDER_STAGES.indexOf(stage);
  if (rank === -1) return stage === REJECTED_STAGE ? [1, 1, ""] : [1, 0, stage.toLowerCase()];
  return [0, -rank, ""];
}

/**
 * The chips' order: ladder rungs furthest first, then off-ladder A-Z with
 * Rejected last, then Unknown — the backend's `stage_sort_key`, descending on
 * the ladder. Exported for its unit test.
 */
export function stageChipOrder(stages: readonly string[]): string[] {
  return [...stages].sort((a, b) => {
    const [ga, ra, la] = chipSortKey(a);
    const [gb, rb, lb] = chipSortKey(b);
    // Code-unit order, as Python compares the lowered labels — `localeCompare`
    // would place accented or apostrophised names differently from the rows.
    return ga - gb || ra - rb || (la < lb ? -1 : la > lb ? 1 : 0);
  });
}

export function StageChips({ distribution, selected, onToggle }: StageChipsProps) {
  const counts = new Map(distribution.map((bucket) => [bucket.label, bucket.count]));
  const stages = stageChipOrder([...counts.keys()]);
  if (stages.length === 0) return null;
  return (
    // A `<fieldset>`, which is a group by nature (biome's a11y rule prefers
    // the element to the role); Tailwind's preflight strips its border and
    // padding, and the legend is for screen readers only.
    <fieldset data-testid="candidates-stage-chips" className="flex flex-wrap items-center gap-2">
      <legend className="sr-only">Filter by stage</legend>
      {stages.map((stage) => {
        const pressed = selected.includes(stage);
        return (
          <button
            key={stage}
            type="button"
            data-testid="candidates-stage-chip"
            data-stage={stage}
            aria-pressed={pressed}
            onClick={() => onToggle(stage)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-label transition-colors ${
              pressed
                ? "border-accent bg-accent-muted text-accent"
                : "border-border text-text-secondary hover:border-border-hover hover:text-text"
            }`}
          >
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${stageBgClass(stage)}`}
            />
            {stage}
            <span className="tabular-nums text-text-muted">{counts.get(stage)}</span>
          </button>
        );
      })}
    </fieldset>
  );
}
